import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import FeriadoService from "../../services/resource/FeriadoService";
import { ANOS } from "../../utils/options";

// feriados nacionais de data fixa (os móveis, como Carnaval e Corpus Christi, são cadastrados à mão)
const NACIONAIS_FIXOS = [
    ["01-01", "Confraternização Universal"],
    ["04-21", "Tiradentes"],
    ["05-01", "Dia do Trabalho"],
    ["09-07", "Independência do Brasil"],
    ["10-12", "Nossa Senhora Aparecida"],
    ["11-02", "Finados"],
    ["11-15", "Proclamação da República"],
    ["12-25", "Natal"],
];

const SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

const fmtData = (iso) => {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const diaDaSemana = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return SEMANA[new Date(y, m - 1, d).getDay()];
};

/**
 * Feriados que não contam como dia útil no prazo de acerto dos romaneios (DA). Sábado e domingo nunca contam.
 */
export default function FeriadosModal({ visible, onHide, anoInicial }) {
    const service = useMemo(() => new FeriadoService(), []);
    const [ano, setAno] = useState(anoInicial ?? new Date().getFullYear());
    const [feriados, setFeriados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState("");
    const [descricao, setDescricao] = useState("");

    const carregar = useCallback(() => {
        setLoading(true);
        service.listar(ano)
            .then(res => setFeriados(res.data ?? []))
            .catch(() => toast.error("Erro ao carregar os feriados."))
            .finally(() => setLoading(false));
    }, [service, ano]);

    useEffect(() => {
        if (visible) carregar();
    }, [visible, carregar]);

    const erroDe = (e, padrao) => e?.response?.data?.message || padrao;

    const adicionar = () => {
        if (!data) {
            toast.warning("Informe a data do feriado.");
            return;
        }
        service.cadastrar({ data, descricao })
            .then(() => {
                setData("");
                setDescricao("");
                // se a data é de outro ano, vai para esse ano para o usuário ver o que cadastrou
                const anoDaData = Number(data.slice(0, 4));
                if (anoDaData !== ano) setAno(anoDaData); else carregar();
            })
            .catch(e => toast.error(erroDe(e, "Erro ao cadastrar o feriado.")));
    };

    const remover = (feriado) => {
        service.remover(feriado.id)
            .then(carregar)
            .catch(e => toast.error(erroDe(e, "Erro ao remover o feriado.")));
    };

    const adicionarNacionais = async () => {
        const existentes = new Set(feriados.map(f => f.data));
        const novos = NACIONAIS_FIXOS
            .map(([md, nome]) => ({ data: `${ano}-${md}`, descricao: nome }))
            .filter(f => !existentes.has(f.data));

        if (!novos.length) {
            toast.info("Os feriados nacionais fixos deste ano já estão cadastrados.");
            return;
        }
        try {
            for (const f of novos) await service.cadastrar(f);
            toast.success(`${novos.length} feriado(s) nacional(is) adicionado(s).`);
        } catch (e) {
            toast.error(erroDe(e, "Erro ao cadastrar os feriados nacionais."));
        }
        carregar();
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            blockScroll
            className="w-[640px] max-w-[95%]"
            header={
                <div className="flex items-center gap-2 text-base font-bold text-foreground">
                    <i className="fa fa-calendar-times text-primary" />
                    Feriados (prazo de acerto dos romaneios)
                </div>
            }
        >
            <div className="space-y-4">
                <p className="rounded-lg border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
                    <i className="fa fa-info-circle mr-1.5 text-primary" />
                    O prazo conta só dias úteis de segunda a sexta, descontando os feriados cadastrados aqui. Depois de
                    alterar, gere a comissão DA novamente para recalcular os prazos dos romaneios.
                </p>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[130px_1fr_auto] sm:items-end">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-foreground/80">Data</label>
                        <input
                            type="date"
                            value={data}
                            onChange={e => setData(e.target.value)}
                            className="rounded-lg border border-input bg-card px-2 py-2 text-sm text-foreground outline-none"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-foreground/80">Descrição</label>
                        <input
                            type="text"
                            value={descricao}
                            onChange={e => setDescricao(e.target.value)}
                            placeholder="Ex.: Nossa Senhora Aparecida"
                            className="rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={adicionar}
                        className="inline-flex h-[38px] items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:brightness-95"
                    >
                        <i className="fa fa-plus text-xs" /> Adicionar
                    </button>
                </div>

                <div className="flex items-center justify-between gap-2">
                    <select
                        value={ano}
                        onChange={e => setAno(Number(e.target.value))}
                        className="rounded-lg border border-input bg-card px-3 py-1.5 text-sm text-foreground outline-none"
                    >
                        {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                    <button
                        type="button"
                        onClick={adicionarNacionais}
                        className="text-xs font-semibold text-primary hover:underline"
                    >
                        <i className="fa fa-flag mr-1" />
                        Adicionar feriados nacionais fixos de {ano}
                    </button>
                </div>

                <div className="max-h-72 overflow-auto rounded-lg border border-border">
                    {loading ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                            <i className="fa fa-spinner fa-spin mr-2" />
                            Carregando...
                        </p>
                    ) : feriados.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                            Nenhum feriado cadastrado em {ano}.
                        </p>
                    ) : (
                        <table className="w-full text-xs">
                            <tbody>
                                {feriados.map(f => (
                                    <tr key={f.id} className="border-b border-dashed border-border/60 last:border-0">
                                        <td className="px-3 py-2 font-semibold tabular-nums text-foreground">{fmtData(f.data)}</td>
                                        <td className="px-2 py-2 capitalize text-muted-foreground">{diaDaSemana(f.data)}</td>
                                        <td className="px-2 py-2 text-foreground">{f.descricao || "—"}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                type="button"
                                                onClick={() => remover(f)}
                                                title="Remover feriado"
                                                className="text-destructive hover:opacity-70"
                                            >
                                                <i className="fa fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </Dialog>
    );
}
